import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Check, Minus } from "lucide-react";

const EMPTY = { name: "", price: 0, is_default: false };
const STEP = 5;

export default function AdminShippingClasses() {
  const [classes, setClasses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [flashId, setFlashId] = useState(null);

  const load = () => api.get("/shipping-classes").then((r) => setClasses(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => setEditing({ ...EMPTY });
  const startEdit = (c) => setEditing({ ...c });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (!editing.id) {
        await api.post("/admin/shipping-classes", editing);
        toast.success("Class created");
      } else {
        const { id, created_at, ...payload } = editing;
        await api.put(`/admin/shipping-classes/${id}`, payload);
        toast.success("Saved");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (c) => {
    if (!confirm(`Delete "${c.name}"? Products using it will fall back to the default class.`)) return;
    try {
      await api.delete(`/admin/shipping-classes/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const set = (k, v) => setEditing((s) => ({ ...s, [k]: v }));

  const adjustPrice = async (c, delta) => {
    const next = Math.max(0, Number((c.price + delta).toFixed(2)));
    if (next === c.price) return;
    // Optimistic update
    setClasses((cs) => cs.map((x) => (x.id === c.id ? { ...x, price: next } : x)));
    setSavingId(c.id);
    try {
      await api.put(`/admin/shipping-classes/${c.id}`, { price: next });
      setFlashId(c.id);
      setTimeout(() => setFlashId((cur) => (cur === c.id ? null : cur)), 900);
    } catch (e) {
      // Revert
      setClasses((cs) => cs.map((x) => (x.id === c.id ? { ...x, price: c.price } : x)));
      toast.error(formatErr(e));
    } finally {
      setSavingId((cur) => (cur === c.id ? null : cur));
    }
  };

  return (
    <div data-testid="admin-shipping-page">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl text-[#F5F5DC]">Shipping Classes</h1>
        <button onClick={startCreate} className="btn-primary" data-testid="new-shipping-class-btn">
          <Plus size={14} /> New Class
        </button>
      </div>
      <p className="text-[#A3A3A3] text-sm mb-10 max-w-2xl">
        Each product is assigned a shipping class. Shipping at checkout is the sum of each item&apos;s class price ×
        quantity. Local Invercargill pickup is always free. The class marked default is used for any product without a class assigned.
      </p>

      <div className="bg-[#2A2A2E] border border-[#3A3A3E] rounded-md overflow-hidden">
        {classes.map((c, idx) => {
          const flashing = flashId === c.id;
          const saving = savingId === c.id;
          return (
            <div
              key={c.id}
              className={`p-5 ${idx < classes.length - 1 ? "border-b border-[#3A3A3E]" : ""} transition-colors duration-300 ${flashing ? "bg-emerald-800/40" : "bg-[#2A2A2E]"}`}
              data-testid={`shipping-row-${c.id}`}
            >
              {/* Row 1: Name + edit/delete actions */}
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <p className="font-display text-2xl !text-white font-bold flex-1 min-w-0">{c.name}</p>
                {c.is_default && (
                  <span className="text-[10px] uppercase tracking-widest text-[#F5C89E] bg-[#B87333]/20 border border-[#B87333] px-2 py-0.5 font-bold">
                    Default
                  </span>
                )}
                <button onClick={() => startEdit(c)} className="text-[#F5F5DC] hover:text-[#B87333] p-2" data-testid={`edit-shipping-${c.id}`} aria-label="Edit">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => del(c)} className="text-[#F5F5DC] hover:text-[#CF6679] p-2" data-testid={`delete-shipping-${c.id}`} aria-label="Delete">
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Row 2: Quick adjust controls */}
              <div className="flex items-center justify-center gap-5 sm:gap-8 py-2">
                <button
                  onClick={() => adjustPrice(c, -STEP)}
                  disabled={saving || c.price <= 0}
                  className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-[#F5F5DC] border-2 border-[#F5F5DC] hover:bg-[#B87333] hover:border-[#B87333] hover:text-white active:scale-95 transition-all text-[#1a1a1a] font-bold disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation rounded-md shadow-md"
                  data-testid={`shipping-decrement-${c.id}`}
                  aria-label={`Decrease ${c.name} by $${STEP}`}
                >
                  <Minus size={28} strokeWidth={3} />
                </button>

                <div className="flex flex-col items-center min-w-[140px]">
                  <p
                    className={`font-display text-4xl sm:text-5xl font-black tabular-nums leading-none transition-colors duration-300 ${flashing ? "!text-emerald-300" : "!text-white"}`}
                    data-testid={`shipping-price-${c.id}`}
                  >
                    ${c.price.toFixed(2)}
                  </p>
                  <span className="text-[11px] uppercase tracking-widest text-[#F5F5DC] mt-1 font-bold">
                    {flashing ? "Saved" : `±$${STEP}`}
                  </span>
                </div>

                <button
                  onClick={() => adjustPrice(c, STEP)}
                  disabled={saving}
                  className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-[#F5F5DC] border-2 border-[#F5F5DC] hover:bg-[#B87333] hover:border-[#B87333] hover:text-white active:scale-95 transition-all text-[#1a1a1a] font-bold disabled:opacity-40 disabled:cursor-not-allowed touch-manipulation rounded-md shadow-md"
                  data-testid={`shipping-increment-${c.id}`}
                  aria-label={`Increase ${c.name} by $${STEP}`}
                >
                  <Plus size={28} strokeWidth={3} />
                </button>
              </div>
            </div>
          );
        })}
        {classes.length === 0 && <p className="p-12 text-center text-[#F5F5DC]">No shipping classes yet.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 flex items-end sm:items-center justify-center z-50 sm:p-4" data-testid="shipping-modal">
          <div className="bg-[#0A0A0A] sm:border sm:border-[#2A2A2A] sm:max-w-md w-full flex flex-col">
            <div className="p-5 sm:p-6 border-b border-[#2A2A2A] flex justify-between items-center">
              <h2 className="font-display text-2xl text-[#F5F5DC]">{editing.id ? "Edit Class" : "New Class"}</h2>
              <button onClick={() => setEditing(null)} className="text-[#A3A3A3] p-2 -mr-2"><X size={22} /></button>
            </div>
            <div className="p-5 sm:p-6 space-y-4">
              <div>
                <label className="smallcaps block mb-1">Name</label>
                <input data-testid="sc-name" className="input-dark" value={editing.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Extra Large" />
              </div>
              <div>
                <label className="smallcaps block mb-1">Price (NZD)</label>
                <input data-testid="sc-price" type="number" step="0.01" min="0" className="input-dark" value={editing.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} />
              </div>
              <label className="flex items-center gap-3 text-sm text-[#A3A3A3] cursor-pointer">
                <input type="checkbox" checked={!!editing.is_default} onChange={(e) => set("is_default", e.target.checked)} data-testid="sc-default" />
                Use as default class for new / unassigned products
              </label>
            </div>
            <div className="p-5 sm:p-6 border-t border-[#2A2A2A] flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-ghost px-4">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="sc-save"><Check size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
